import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { portalPublicService } from '../../api/portal';
import { PortalContext, PortalDocument } from '../../types';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

/**
 * Portail client (TF-042 → TF-046) — page PUBLIQUE, hors coquille authentifiée.
 * Le client consulte ses devis/factures/avoirs, accepte un devis, paie en ligne (lien existant),
 * et signale un litige. Accès via un jeton opaque dans l'URL.
 */
export function PortailClient() {
  const { token = '' } = useParams<{ token: string }>();
  const [ctx, setCtx] = useState<PortalContext | null>(null);
  const [docs, setDocs] = useState<PortalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      portalPublicService.getContext(token).then(setCtx),
      portalPublicService.getDocuments(token).then(setDocs),
    ]).catch(() => setError('Lien invalide ou expiré.')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const accept = async (id: number) => {
    if (!window.confirm('Confirmer l’acceptation de ce devis ?')) return;
    await portalPublicService.accept(token, id);
    load();
  };
  const dispute = async (id: number) => {
    const motif = window.prompt('Décrivez le problème rencontré :');
    if (!motif) return;
    await portalPublicService.dispute(token, id, motif);
    load();
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>;
  if (error || !ctx) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="card text-center max-w-md">
          <p className="text-lg font-semibold text-gray-800">Accès indisponible</p>
          <p className="text-gray-500 mt-2">{error ?? 'Lien invalide.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <p className="text-xs text-gray-400 uppercase tracking-wide">Espace client</p>
          <h1 className="text-xl font-bold text-gray-900">{ctx.societe}</h1>
          <p className="text-sm text-gray-500">Bonjour {ctx.client}</p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {docs.length === 0 ? (
          <div className="card text-center text-gray-500">Aucun document disponible pour le moment.</div>
        ) : docs.map((d) => (
          <div key={d.id} className="card">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-gray-900">{labelType(d.type)} {d.numero}</p>
                <p className="text-xs text-gray-500">
                  {d.dateDocument}{d.dateEcheance ? ` · échéance ${d.dateEcheance}` : ''}
                </p>
                <div className="mt-1 flex items-center gap-2">
                  <StatusBadge statut={d.statut} />
                  {d.type === 'FACTURE' && <span className="text-xs text-gray-500">{labelPaiement(d.statutPaiement)}</span>}
                </div>
              </div>
              <div className="text-right">
                <MoneyDisplay amount={d.netAPayer} size="lg" className="font-bold" />
                {d.type === 'FACTURE' && d.resteDu > 0 &&
                  <p className="text-xs text-gray-500">Reste dû : {d.resteDu.toLocaleString('fr-FR')}</p>}
                {d.totalCo2 > 0 &&
                  <p className="text-xs text-emerald-600 mt-1">{d.totalCo2.toLocaleString('fr-FR')} kg CO₂e</p>}
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              {d.accepterPossible &&
                <button onClick={() => accept(d.id)} className="btn-primary text-sm">Accepter le devis</button>}
              {d.payable && d.paymentUrl &&
                <a href={d.paymentUrl} target="_blank" rel="noreferrer" className="btn-primary text-sm">Payer en ligne</a>}
              <button onClick={() => dispute(d.id)} className="btn-secondary text-sm">Signaler un problème</button>
            </div>
          </div>
        ))}
        <p className="text-center text-xs text-gray-400 pt-4">Propulsé par TuniFact</p>
      </main>
    </div>
  );
}

function labelType(t: string): string {
  return t === 'DEVIS' ? 'Devis' : t === 'FACTURE' ? 'Facture' : t === 'AVOIR' ? 'Avoir' : t;
}
function labelPaiement(s: string): string {
  return ({
    NON_PAYEE: 'Non payée', PARTIELLEMENT_PAYEE: 'Partiellement payée',
    PAYEE: 'Payée', EN_RETARD: 'En retard',
  } as Record<string, string>)[s] ?? s;
}
function StatusBadge({ statut }: { statut: string }) {
  const map: Record<string, string> = {
    ACCEPTE: 'bg-green-100 text-green-700',
    ENVOYE: 'bg-blue-100 text-blue-700',
    REFUSE: 'bg-red-100 text-red-700',
    EXPIRE: 'bg-gray-100 text-gray-600',
    VALIDE: 'bg-emerald-100 text-emerald-700',
    BROUILLON: 'bg-gray-100 text-gray-600',
    ANNULE: 'bg-red-100 text-red-700',
  };
  const label = statut.charAt(0) + statut.slice(1).toLowerCase();
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[statut] ?? 'bg-gray-100 text-gray-600'}`}>{label}</span>;
}

export default PortailClient;
